/**
 * TanStack Query hooks of the links screens (1.J deliverable 1). Keys: `['links', 'list', docId,
 * direction, typeKey]`, `['links', 'types']`, `['links', 'titles', sortedIds]`; the story tree
 * under 1.I-1's `['tree', parentId ?? null]` and `docs.meta` under 1.I-3's `['documents',
 * 'meta', id]`, so one answer serves both. A 401 reaches the query client's handler.
 */
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { type ListOptions, useLinksApi } from './api.js';

export const listKey = (docId: string, options: ListOptions = {}) =>
  ['links', 'list', docId, options.direction ?? 'both', options.linkTypeKey ?? 'all'] as const;
export const typesKey = ['links', 'types'] as const;
export const titlesKey = (ids: readonly string[]) => ['links', 'titles', [...ids].sort()] as const;
export const treeKey = (parentId: string | null) => ['tree', parentId] as const;
export const metaKey = (id: string) => ['documents', 'meta', id] as const;

/**
 * `enabled: false` only when off: an explicit `true` would override the shell client's default,
 * which holds every read once a sign-out has begun.
 */
const whenOff = (enabled: boolean) => (enabled ? {} : { enabled: false });

export function useLinkList(docId: string, options: ListOptions = {}, enabled = true) {
  const api = useLinksApi();
  return useQuery({
    queryKey: listKey(docId, options),
    queryFn: () => api.list(docId, options),
    ...whenOff(enabled),
    // The panel has its own "Try again".
    retry: false,
  });
}

export function useLinkTypes() {
  const api = useLinksApi();
  return useQuery({ queryKey: typesKey, queryFn: () => api.types(), staleTime: 300_000 });
}

/** `docs.titles` once per distinct id set. */
export function useTitles(ids: readonly string[]) {
  const api = useLinksApi();
  const distinct = [...new Set(ids)];
  return useQuery({
    queryKey: titlesKey(distinct),
    queryFn: () => api.titles(distinct),
    ...whenOff(distinct.length > 0),
  });
}

export function useTree(parentId: string | null, enabled = true) {
  const api = useLinksApi();
  return useQuery({
    queryKey: treeKey(parentId),
    queryFn: () => api.tree(parentId),
    ...whenOff(enabled),
  });
}

export function useDocMeta(id: string, enabled = true) {
  const api = useLinksApi();
  return useQuery({ queryKey: metaKey(id), queryFn: () => api.meta(id), ...whenOff(enabled) });
}

/** `docs.meta` of several pages, one read each through the shared cache. */
export function useDocMetas(ids: readonly string[]) {
  const api = useLinksApi();
  return useQueries({
    queries: ids.map((id) => ({ queryKey: metaKey(id), queryFn: () => api.meta(id) })),
  });
}

export const backlinksKey = (docId: string) => ['links', 'backlinks', docId] as const;
export const unlinkedKey = (docId: string) => ['links', 'unlinked', docId] as const;

/**
 * Backlinks and Unlinked change when another page is written (a mention typed there), and that
 * publishes no event about this page; the live refresh reaches them only while the tab is open.
 * So every visit reads them again instead of showing an answer up to 30 s old (DECISIONS 448):
 * `staleTime: 0` rather than `refetchOnMount`, because a tab that stays mounted while the page
 * changes switches its key, and that refetches only an answer that is stale.
 */
const EVERY_VISIT = { staleTime: 0 } as const;

export function useBacklinks(docId: string) {
  const api = useLinksApi();
  return useQuery({
    queryKey: backlinksKey(docId),
    queryFn: () => api.backlinks(docId),
    ...EVERY_VISIT,
    retry: false,
  });
}

/** `search.unlinked`, 50 at a time while `next` is set. */
export function useUnlinked(docId: string) {
  const api = useLinksApi();
  return useInfiniteQuery({
    queryKey: unlinkedKey(docId),
    queryFn: ({ pageParam }) => api.unlinked(docId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    ...EVERY_VISIT,
    retry: false,
  });
}
